import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";
import Logo from "./Logo";
import { useCashApp } from "./CashAppModal";

const LINKS = [
  { name: "Home", to: "/" },
  { name: "Programs", to: "/programs" },
  { name: "Events & Media", to: "/media" },
  { name: "Partner With Us", to: "/partner" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { open } = useCashApp();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-6 pt-3">
        <div
          className={`max-w-7xl mx-auto rounded-2xl glass flex items-center justify-between pl-4 pr-3 sm:pl-6 sm:pr-4 h-16 transition-shadow duration-300 ${
            scrolled ? "shadow-lg shadow-purple-200/60" : ""
          }`}
        >
          <Link to="/" data-testid="nav-logo-link" aria-label="Y.E.S. home">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative text-sm font-semibold transition-colors ${
                    isActive ? "text-purple-800" : "text-slate-600 hover:text-purple-800"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.name}
                    {isActive && (
                      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-purple-600" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={open} data-testid="nav-support-btn" className="btn-primary px-5 py-2.5 text-sm">
              <Heart className="w-4 h-4" /> Invest in Our Youth
            </button>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
              className="md:hidden w-11 h-11 rounded-xl flex items-center justify-center text-purple-800 hover:bg-purple-50 transition-colors"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-white/95 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="h-full flex flex-col justify-center px-8 pt-16">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className="block py-4 font-display text-4xl font-extrabold tracking-tight text-purple-900"
                  >
                    {l.name}
                  </Link>
                </motion.div>
              ))}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-8 text-sm text-slate-500"
              >
                St. Louis, MO ·{" "}
                <a href="mailto:daneisha.green1@yahoo.com" className="text-purple-700 font-semibold" data-testid="mobile-email-link">
                  daneisha.green1@yahoo.com
                </a>
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;